import { useEffect, useMemo, useState, useRef } from 'react';
/**
 * Given a product, this hook provides methods for getting and setting variants on that product,
 * as well as methods to validate the current and proposed combination of values.
 */
export function useProductVariants(product, { initialVariant } = {}) {
    var _a, _b, _c, _d;
    const variantOptions = (_b = (_a = product) === null || _a === void 0 ? void 0 : _a.variants) === null || _b === void 0 ? void 0 : _b.options;
    const variantValues = (_d = (_c = product) === null || _c === void 0 ? void 0 : _c.variants) === null || _d === void 0 ? void 0 : _d.values;
    // Results in the following data structure:
    // { "Black:Small": { …variant } }
    const normalisedValues = useMemo(() => {
        if (!variantValues)
            return;
        return variantValues.reduce((acc, curr) => {
            const valueIdentifier = curr.values.join(':');
            acc[valueIdentifier] = curr;
            return acc;
        }, {});
    }, [variantValues]);
    const [selectedValues, selectValues] = useVariantState({
        variantOptions,
        initialVariant
    });
    const selectedVariant = useMemo(() => {
        return getSelectedVariant(normalisedValues, selectedValues);
    }, [normalisedValues, selectedValues]);
    const validation = useMemo(() => {
        if (!variantOptions || variantOptions.length === 0)
            return 'valid';
        const selectedValueLength = selectedValues.filter(Boolean).length;
        const allValuesSelected = variantOptions.length === selectedValueLength;
        const invalidValueCombination = allValuesSelected && !selectedVariant;
        if (invalidValueCombination) {
            return 'invalid';
        }
        if (!allValuesSelected)
            return 'incomplete';
        const variantIsInStock = selectedVariant.stockStatus.buyable;
        return variantIsInStock ? 'valid' : 'outOfStock';
    }, [selectedValues, selectedVariant, variantOptions]);
    function getSelectedValue(option) {
        const optionIndex = variantOptions.indexOf(option);
        return selectedValues[optionIndex];
    }
    function selectValue(value, option) {
        const selectedIndex = variantOptions.indexOf(option);
        const currentValueForOption = selectedValues[selectedIndex];
        const valueIsValid = valueIsValidForOption(variantOptions, option, value);
        if (!valueIsValid) {
            throw new Error(`${value} is not a valid value for ${option.name}`);
        }
        if (currentValueForOption === value)
            return;
        selectValues(state => {
            const newState = [...state];
            newState[selectedIndex] = value;
            return newState;
        });
    }
    /**
     * Takes a value and an option and checks if the value is selectable for that option.
     */
    function validateSelection(value, option) {
        const totalOptionsLength = variantOptions.length;
        const selectedValuesLength = selectedValues.filter(Boolean).length;
        const optionLevel = variantOptions.indexOf(option);
        const selectedValue = selectedValues[optionLevel];
        if (!valueIsValidForOption(variantOptions, option, value)) {
            return 'invalid';
        }
        if (selectedValuesLength < totalOptionsLength - 1) {
            return 'incomplete';
        }
        if (selectedValue && selectedValuesLength !== totalOptionsLength) {
            return 'incomplete';
        }
        const variant = getProposedVariant({
            currentState: selectedValues,
            variantTable: normalisedValues,
            optionLevel,
            value
        });
        if (!variant)
            return 'invalid';
        const variantIsBuyable = variant.stockStatus.buyable;
        if (!variantIsBuyable)
            return 'outOfStock';
        return 'valid';
    }
    function getVariantForSelection(value, option) {
        return getProposedVariant({
            currentState: selectedValues,
            variantTable: normalisedValues,
            optionLevel: variantOptions.indexOf(option),
            value
        });
    }
    function getMissingOptions() {
        const noneMissing = selectedValues.length === variantOptions.length;
        const allMissing = selectedValues.length === 0;
        if (noneMissing)
            return null;
        if (allMissing)
            return variantOptions;
        const unselectedOptions = variantOptions.filter((_, index) => {
            const optionIsNotSelected = typeof selectedValues[index] === 'undefined';
            return optionIsNotSelected;
        });
        return unselectedOptions;
    }
    return {
        selectedVariant,
        validation,
        getSelectedValue,
        selectValue,
        validateSelection,
        getVariantForSelection,
        getMissingOptions
    };
}
function useVariantState({ variantOptions, initialVariant }) {
    const defaultValues = useMemo(() => getDefaultValues(variantOptions, initialVariant), [initialVariant, variantOptions]);
    const [selectedValues, selectValues] = useState(defaultValues);
    // TODO: remove this when products are not mocked on product routes
    useDefaultVariant({
        defaultValues,
        selectValues
    });
    return [selectedValues, selectValues];
}
/**
 * Determine the initially selected values for the product's variant options
 * - If an initialVariant is provided, use it
 * - If there are options that have only 1 value, select them
 * - Otherwise, select nothing
 */
function getDefaultValues(options, initialVariant) {
    if (initialVariant)
        return initialVariant.values;
    if (!options)
        return [];
    const optionsWithSingleValue = options
        .map((option, index) => {
        return option.values.length === 1
            ? { value: option.values[0], index }
            : null;
    })
        .filter(Boolean);
    if (optionsWithSingleValue.length === 0)
        return [];
    const defaultValues = [];
    for (const { value, index } of optionsWithSingleValue) {
        defaultValues[index] = value;
    }
    return defaultValues;
}
function getSelectedVariant(normalisedValues, selectedValues) {
    var _a;
    return (_a = normalisedValues) === null || _a === void 0 ? void 0 : _a[selectedValues.join(':')];
}
// TODO: Remove this when mock products aren't passed to the product page
function useDefaultVariant({ defaultValues, selectValues }) {
    const hasSetDefault = useRef();
    // Currently, the product page receives a 'mock product' on the initial
    // render when the product hasn't loaded yet. This means that
    // useProductVariants is executed once with a mock product
    // As a result, the default state is calculated with that mock product's variants.
    // Then, the component re-renders with an actual product, but since `useState` was already initialised, it doesn't initialise again.
    // This means that the `initialVariant` is not set on client renders (nor does it default to selecting a variant option with 1 value)
    // The only way around this is to manually set with useEffect…
    useEffect(() => {
        if (defaultValues.length === 0)
            return;
        if (!hasSetDefault.current) {
            selectValues(defaultValues);
            hasSetDefault.current = true;
        }
    }, [defaultValues, selectValues]);
}
function valueIsValidForOption(variantOptions, option, value) {
    const optionLevel = variantOptions.indexOf(option);
    const possibleValues = variantOptions[optionLevel].values;
    const isValid = possibleValues.includes(value);
    return isValid;
}
function getProposedVariant({ currentState, variantTable, optionLevel, value }) {
    const proposedState = [...currentState];
    proposedState[optionLevel] = value;
    const variant = getSelectedVariant(variantTable, proposedState);
    return variant;
}
//# sourceMappingURL=useProductVariants.js.map